<template>
  <div>
    <!--- region STOCK INFO --->
    <template v-if="hasStock">
      <Teleport v-if="portalTargetStock" :to="`#${portalTargetStock}`" :class="innerClassStock">
        <adc-stock-radio
          v-model="stockType"
          :product-data="productData"
          :disabled="!isStockChangeAllowed"
          :non-input="!allowCartItemOperations"
        />
      </Teleport>
      <div v-else :class="innerClassStock">
        <adc-stock-radio
          v-model="stockType"
          :product-data="productData"
          :disabled="!isStockChangeAllowed"
          :non-input="!allowCartItemOperations"
        />
      </div>
    </template>
    <!--- endregion STOCK INFO --->
    <div v-if="isStatusVisible && showedStatusMessageFull" :class="innerClassStatus">
      <div v-html="showedStatusMessageFull.shortText ? $awt(showedStatusMessageFull.shortText) : ''" />
    </div>

    <template v-else-if="allowCartItemOperations">
      <!--- region QUANTITY TYPE CHOSER --->
      <div v-if="showUnitSelector" :class="innerClassUnit">
        <aw-tiny-radio
          v-model="quantityType"
          :legend-text="$awt('aw.common.add_to_basket.unit_selector')"
          :disabled="addToCartLoading || updateCartLoading"
          :options="quantityTypeOptions"
          option-id-property="code"
          option-label-property="label"
          :is-small="isUnitSelectorSmall"
          :direction="unitSelectorDirection"
          @update:model-value="quantityType = $event.code"
        />
      </div>
      <!--- region QUANTITY CHOSER --->
      <div :class="innerClassQuantity">
        <aw-tiny-plus-minus-chooser
          :legend-text="$awt('aw.common.add_to_basket.quantity_selector')"
          :visible-text="quantityVisibleText"
          :disabled="addToCartLoading || updateCartLoading"
          :is-increase-disabled="!hasNextQuantitySep"
          :is-decrease-disabled="!hasPrevQuantitySep"
          :increase-label="$awt('aw.common.quantity_chooser.aria_label_increase',{product_name: productName, quantity: quantityOptions.length ? quantityOptions[0].name: '0'})"
          :decrease-label="$awt('aw.common.quantity_chooser.aria_label_decrease',{product_name: productName, quantity: quantityOptions.length ? quantityOptions[0].name: '0'})"
          :is-small="isQuantitySelectorSmall"
          :is-double-sided="isQuantitySelectorDoubleSided"
          :direction="quantitySelectorDirection"
          :is-double-sided-small="isQuantitySelectorDoubleSidedSmall"
          @change="changeQuantity"
        />
      </div>
      <!--- endregion QUANTITY CHOSER --->
      <!--- region ADD TO BASKET --->
      <div v-if="isCartButtonVisible" :class="innerClassCartBtn">
        <lv-button
          :styling="`${smallCartBtn ? 'small-' : ''}primary${basketItem ? '-light' : '-dark'}${isButtonIconVisible && !isButtonTextVisible ? '-just-icon' : ''}`"
          :aria-pressed="(!!basketItem).toString()"
          :loading="addToCartLoading || updateCartLoading"
          :loading-id="[addToCartItemId, updateCartItemId, removeCartItemId]"
          :aria-label="$awt(basketItem ? 'aw.common.add_to_basket.aria_label_remove_from_cart' : 'aw.common.add_to_basket.aria_label_add_to_cart', { product_name: productName })"
          :class="[
            $style.cartBtn,
            {
              [$style.cartBtnWithText]: isButtonTextVisible && !noForceCartBtnPadding,
            },
          ]"
          @click="cartButtonEvt"
        >
          <span v-if="isButtonTextVisible" v-text="$awt(basketItem ? 'aw.common.add_to_basket.remove_from_cart' : 'aw.common.add_to_basket.to_cart')" />
          <lv-icon v-if="isButtonIconVisible" :name="basketItem ? 'trash-version2-16' : 'shopping-cart-24'" :size="24" />
        </lv-button>
      </div>
      <!--- endregion ADD TO BASKET --->
    </template>
  </div>
</template>

<script>
  import { defineAsyncComponent } from 'vue';
  import { useVuelidate } from '@vuelidate/core';
  import { LvIcon, LvButton } from '~~/common/components/loginet-vue-shop/index.mjs';
  import basketControlMixin from '~~/common/components/Page/Common/BasketControl/basketControlMixin.js';

  export default {
    name: 'AwAddToBasketVersion2',
    components: {
      LvIcon,
      LvButton,
      AdcStockRadio: defineAsyncComponent(() => import('~~/dealer/components/AdcStockRadio.vue')),
      AwTinyRadio: defineAsyncComponent(() => import('~~/common/components/AwTinyRadio.vue')),
      AwTinyPlusMinusChooser: defineAsyncComponent(() => import('~~/common/components/AwTinyPlusMinusChooser.vue')),
    },
    mixins: [
      basketControlMixin,
    ],
    props: {
      portalTargetStock: {
        type: String,
        required: true,
      },
      isStatusVisible: {
        type: Boolean,
        required: true,
      },
      isCartButtonVisible: {
        type: Boolean,
        required: true,
      },
      isUnitSelectorVisible: {
        type: Boolean,
        required: true,
      },
      isStockChangeAllowed: {
        type: Boolean,
        required: true,
      },
      isButtonTextVisible: {
        type: Boolean,
        required: true,
      },
      isButtonIconVisible: {
        type: Boolean,
        required: true,
      },
      isUnitSelectorSmall: {
        type: Boolean,
        required: true,
      },
      isQuantitySelectorSmall: {
        type: Boolean,
        required: true,
      },
      isQuantitySelectorDoubleSided: {
        type: Boolean,
        required: true,
      },
      isQuantitySelectorDoubleSidedSmall: {
        type: Boolean,
        required: false,
        default: false,
      },
      quantitySelectorDirection: {
        type: String,
        required: true,
        validator: v => v === 'horizontal' || v === 'vertical',
      },
      unitSelectorDirection: {
        type: String,
        required: false,
        validator: v => v === 'horizontal' || v === 'vertical',
        default: 'vertical',
      },
      innerClassStock: {
        type: String,
        required: true,
      },
      innerClassUnit: {
        type: String,
        required: true,
      },
      innerClassQuantity: {
        type: String,
        required: true,
      },
      innerClassCartBtn: {
        type: String,
        required: true,
      },
      innerClassStatus: {
        type: String,
        required: true,
      },
      alwaysShowLoyaltyPrice: {
        type: Boolean,
        default: false,
      },
      smallCartBtn: {
        type: Boolean,
        default: false,
      },
      noForceCartBtnPadding: {
        type: Boolean,
        default: false,
      },
    },
    setup: () => ({ v$: useVuelidate() }),
    data () {
      return {
        isRemoveModalAppears: false,
      };
    },
    computed: {
      showUnitSelector () {
        return this.isUnitSelectorVisible && this.isConvertible;
      },
      quantityVisibleText () {
        const quantityObject = this.quantity.model;
        return (this.showUnitSelector ? quantityObject?.localeCount : quantityObject?.name) || '';
      },
    },
    methods: {
      changeQuantity (num) {
        if (this.basketItem) {
          if (num === 1) {
            this.increase();
          } else if (num === -1) {
            this.decrease();
          }
        } else if (this.quantityTempChange === 2 && num === -1) {
          this.changeTempByNum(-2);
        } else if (this.quantityTempChange === 0 && num === 1) {
          this.changeTempByNum(2);
        } else {
          this.changeTempByNum(num);
        }
      },
      cartButtonEvt () {
        if (this.basketItem) {
          this.removeFromCart();
        } else {
          this.addToCart();
        }
      },
    },
  };
</script>

<style module lang="scss" rel="stylesheet/scss">
.cartBtn {
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: row-reverse;
  justify-content: center;
  max-width: initial;
  gap: 1ch;

  &WithText {
    padding-right: 8px !important;
    padding-left: 8px !important;
  }
}
</style>
